import { Flex } from '@chakra-ui/react';
import { ReactComponent as PhotosIcon } from 'assets/icons/widgetmenu_photos.svg';
import { IMAGE_UPLOADED } from 'constants/mixpanel';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { useAppDispatch, useAppSelector } from 'modules/Editor/store';
import { selectIsUploading } from 'modules/Editor/store/editorSettingsSelector';
import { setIsUploading as setIsFileUploading } from 'modules/Editor/store/editorSettingsSlice';
import { GET_USER_MEDIA_API } from 'modules/User';
import { useImageUpload } from 'modules/User/useImageUpload';
import { ReactElement, memo, useRef, useState } from 'react';
import { useSWRConfig } from 'swr';

import { DragDropArea, GatedDragDropArea } from './DragDropArea';

export const ImageUpload = memo((): ReactElement => {
  const [isOnDrag, setIsOnDrag] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const isUploading = useAppSelector(selectIsUploading);
  const { mutate } = useSWRConfig();
  const dispatch = useAppDispatch();

  const setIsUploading = (isUploading: boolean) => dispatch(setIsFileUploading(isUploading));

  const { hasValidUploadLimits, filterOutInvalidFiles, allowUpload, uploadImageFile, showUploadSuccessToast } =
    useImageUpload({ isUploading });

  const handleUploadBtnClick = () => inputRef?.current?.click();

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isUploading) return;
    if (!e.target.files) return;

    const files = Array.from(e.target.files) as File[];

    const isValidUploadLimits = hasValidUploadLimits(files);
    if (!isValidUploadLimits) return;

    setIsUploading(true);

    const filteredFiles = filterOutInvalidFiles(files);
    const uploadFilePromises = filteredFiles.map((file) => uploadImageFile(file));

    if (uploadFilePromises.length > 0) {
      await Promise.all(uploadFilePromises);

      // refresh the user media list
      mutate(GET_USER_MEDIA_API);
      const uploadedImageCount = uploadFilePromises.length;
      showUploadSuccessToast(uploadedImageCount);
      Mixpanel.track(IMAGE_UPLOADED, { image_count: uploadedImageCount });
    }

    setIsUploading(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    // prevent the dragover event from bubbling up to the canvas dragover event
    e.stopPropagation();

    setIsOnDrag(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    // prevent the dragover event from bubbling up to the canvas dragover event
    e.stopPropagation();

    setIsOnDrag(false);
  };

  const handleFileDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    // prevent the dragover event from bubbling up to the canvas dragover event
    e.stopPropagation();

    if (!allowUpload || !e.dataTransfer.files) {
      setIsOnDrag(false);
      return;
    }

    const files = Array.from(e.dataTransfer.files) as File[];

    const isValidUploadLimits = hasValidUploadLimits(files);
    if (!isValidUploadLimits) {
      setIsOnDrag(false);
      return;
    }

    setIsUploading(true);

    const filteredFiles = filterOutInvalidFiles(files);
    const uploadFilePromises = filteredFiles.map((file) => uploadImageFile(file));

    if (uploadFilePromises.length > 0) {
      await Promise.all(uploadFilePromises);

      // refresh the user media list
      mutate(GET_USER_MEDIA_API);
      const uploadedImageCount = uploadFilePromises.length;
      showUploadSuccessToast(uploadedImageCount);
      Mixpanel.track(IMAGE_UPLOADED, { image_count: uploadedImageCount });
    }

    setIsUploading(false);
    setIsOnDrag(false);
  };

  const dropAreaBgColor = allowUpload && isOnDrag ? 'upgrade.blue.50' : !allowUpload ? 'white-alpha.700' : 'gray.50';

  return (
    <Flex
      m='4'
      p='4'
      h='12rem'
      borderRadius='4'
      flexDir='column'
      justifyContent='space-around'
      alignItems='center'
      bgColor={dropAreaBgColor}
      border='1px dashed'
      borderColor={allowUpload && isOnDrag ? 'blue.700' : 'gray.300'}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleFileDrop}
      data-testid='image-upload-container'
    >
      <PhotosIcon
        width={'48px'}
        height={'48px'}
        style={{
          color: `${allowUpload && isOnDrag ? 'var(--vg-colors-blue-700)' : 'var(--vg-colors-font-500)'}`,
          marginTop: `${allowUpload ? '0' : '8'}`, // less content for the drag and drop area when allowUpload is false, add margin to keep the area height consistent
        }}
      />
      {allowUpload ? (
        <DragDropArea
          isOnDrag={isOnDrag}
          inputRef={inputRef}
          handleFileChange={handleFileChange}
          handleUploadBtnClick={handleUploadBtnClick}
        />
      ) : (
        <GatedDragDropArea />
      )}
    </Flex>
  );
});
ImageUpload.displayName = 'ImageUpload';
